import { Source, Layer, Marker } from 'react-map-gl/mapbox';
import { Box, Typography } from '@mui/material';
import { FloodRegion } from '../../../../models/floodzone.models.tsx';
import { Polygon } from 'geojson';
import { LoadingToast } from '../../../../components/loading-toast/loading-toast.tsx';
import { primary } from '../../../../constants/theme/colors.tsx';
import { Fragment } from 'react';
import { getCentroid } from '../../utils/coords-math.ts';
import { Position } from '../../types/position.ts';

const floodZoneStyles = {
  A: { color: '#0C5486', opacity: 0.7 },
  AE: { color: '#4FAAE9', opacity: 0.7 },
  AH: { color: '#006DB8', opacity: 0.7 },
  'A1-A30': { color: '#006DB8', opacity: 0.55 },
  AO: { color: '#006DB8', opacity: 0.4 },
  AR: { color: '#00B489', opacity: 0.7 },
  'AR/AE': { color: '#00B489', opacity: 0.55 },
  'AR/AO': { color: '#00B489', opacity: 0.4 },
  'AR/A1-A30': { color: '#102F44', opacity: 0.7 },
  'AR/A': { color: '#102F44', opacity: 0.55 },
  A99: { color: '#102F44', opacity: 0.4 },
  V: { color: '#005843', opacity: 0.7 },
  VE: { color: '#005843', opacity: 0.55 },
  'V1-V30': { color: '#005843', opacity: 0.4 },
  X: { color: '#86DAC6', opacity: 0.4 },
  D: { color: '#00FF57', opacity: 0.4 },
};

const getZoneStyle = (fld_zone: string) => {
  return (
    floodZoneStyles[fld_zone as keyof typeof floodZoneStyles] || {
      color: '#000000',
      opacity: 0.5,
    }
  );
};

interface FloodzoneVisualizationProps {
  floodZoneType: string;
  floodZoneData:
    | {
        floodregions: FloodRegion[];
      }
    | undefined;
  loading: boolean;
  error: Error | null;
}

export const FloodzoneVisualization = ({
  floodZoneType,
  floodZoneData,
  loading,
  error,
}: FloodzoneVisualizationProps) => {
  return (
    <>
      <LoadingToast
        message="Loading Flood Data..."
        isLoading={loading}
        isError={!!error}
        errorMessage={error?.message}
        isSuccess={!loading && !error && floodZoneData !== null}
        toastId="floodzone-loading-toast"
      />
      {floodZoneType && (
        <Typography
          sx={{
            color: primary.dark,
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            top: '5%',
            right: '50%',
          }}
        >
          {`Flood Zone Type: ${floodZoneType}`}
        </Typography>
      )}

      {floodZoneData &&
        floodZoneData.floodregions.map((region, index) => {
          const geo = JSON.parse(region.geojson) as Polygon;
          const coordinates: Position[] = geo.coordinates[0] as never;
          const { color, opacity } = getZoneStyle(region.fld_zone);

          return (
            <Fragment key={index}>
              <Source
                key={`floodregion-src-${index}`}
                id={`floodregion-src-${index}`}
                type="geojson"
                data={geo}
              >
                <Layer
                  key={`floodregion-layer-${index}`}
                  id={`floodregion-layer-${index}`}
                  type="fill"
                  paint={{
                    'fill-color': color,
                    'fill-opacity': opacity,
                  }}
                />
                <Layer
                  key={`floodregion-outline-${index}`}
                  id={`floodregion-outline-${index}`}
                  type="line"
                  paint={{
                    'line-color': color,
                    'line-width': 1,
                  }}
                />
              </Source>
              {/* Checking inNaN because some coordinates may be empty */}
              {coordinates &&
                !isNaN(getCentroid(coordinates)[0]) &&
                !isNaN(getCentroid(coordinates)[1]) && (
                  <>
                    <Marker
                      key={`floodregion-marker-${index}`}
                      longitude={getCentroid(coordinates)[0]}
                      latitude={getCentroid(coordinates)[1]}
                      anchor="bottom"
                    >
                      <Box
                        style={{
                          color: 'white',
                          fontSize: '14px',
                        }}
                      >
                        {`${region.fld_zone}`}
                      </Box>
                    </Marker>
                  </>
                )}
            </Fragment>
          );
        })}
    </>
  );
};
